import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, ChevronDown, Check, BookOpen, Users, Award, Smartphone } from 'lucide-react';
import axios from 'axios';

const InstitutionSelection = () => {
  const navigate = useNavigate();
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    fetchInstitutions();
  }, []);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchInstitutions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/institutions/public/all');
      let data = response.data;

      // Sort "Intuit Online Academy" to the top
      data.sort((a, b) => {
        if (a.name === 'Intuit Online Academy') return -1;
        if (b.name === 'Intuit Online Academy') return 1;
        return a.name.localeCompare(b.name);
      });

      setInstitutions(data);
    } catch (error) {
      console.error('Error fetching institutions:', error);
      setError('Failed to load institutions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInstitutionSelect = (institution) => {
    setSelectedInstitution(institution);
    setIsModalOpen(false);
  };

  const handleContinue = () => {
    if (!selectedInstitution) {
      alert('Please select your institution to continue.');
      return;
    }

    // Store selected institution in localStorage
    localStorage.setItem('selectedInstitution', JSON.stringify(selectedInstitution));

    // Navigate to landing page
    navigate('/landing');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium font-sans">Connecting to Intuit Cloud...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-red-100">
          <div className="w-16 h-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Smartphone className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Connectivity Issue</h2>
          <p className="text-slate-600 mb-8">{error}</p>
          <button
            onClick={fetchInstitutions}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      {/* Premium Header */}
      <div className="bg-gradient-to-br from-blue-700 via-indigo-800 to-slate-900 text-white shrink-0 relative overflow-hidden">
        {/* Subtle decorative circles */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full -ml-48 -mb-48 blur-3xl"></div>

        <div className="max-w-6xl mx-auto px-6 py-16 md:py-24 relative z-10">
          <div className="flex flex-col items-center text-center">
            <div className="mb-8">
              <div className="bg-white/10 backdrop-blur-md p-5 rounded-3xl border border-white/20 shadow-2xl inline-block">
                <GraduationCap className="w-12 h-12 text-blue-300" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-black mb-6 tracking-tight">
              INTUIT <span className="text-blue-400">LMS</span>
            </h1>
            <p className="text-xl text-blue-100/80 font-medium max-w-2xl leading-relaxed">
              Transforming theological education through digital innovation and world-class academic resources.
            </p>
          </div>
        </div>
      </div>

      {/* Main Selection Area */}
      <div className="max-w-3xl mx-auto px-6 -mt-10 mb-20 w-full grow flex flex-col items-center">
        <div className="w-full bg-white rounded-[2rem] shadow-2xl shadow-slate-200 p-8 md:p-12 border border-slate-100">
          <div className="max-w-2xl mx-auto text-center mb-10">
            <h2 className="text-3xl font-extrabold text-slate-900 mb-4">Identity Verification</h2>
            <p className="text-slate-500 font-medium">
              Please identify your institution to synchronize your academic records and curriculum.
            </p>
          </div>

          {/* Selection Trigger Restored */}
          <div className="mb-10">
            <label className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3 ml-1">
              Host Institution
            </label>
            <button
              onClick={() => setIsModalOpen(true)}
              className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl p-6 text-left flex items-center justify-between hover:border-blue-200 hover:bg-slate-100/50 transition-all duration-300 group shadow-sm active:scale-[0.99]"
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold shadow-md transition-transform group-hover:scale-105 ${selectedInstitution ? '' : 'bg-slate-300'
                  }`} style={selectedInstitution ? { backgroundColor: selectedInstitution.primaryColor || '#2563eb' } : {}}>
                  {selectedInstitution ? selectedInstitution.name.charAt(0) : <Users className="w-6 h-6 text-white/70" />}
                </div>
                <div>
                  <span className={`block text-lg font-bold transition-colors ${selectedInstitution ? 'text-slate-900' : 'text-slate-400'
                    }`}>
                    {selectedInstitution ? selectedInstitution.name : 'Tap to browse institutions...'}
                  </span>
                  {selectedInstitution && (
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">
                      {selectedInstitution.learningMode === 'instructor_led' ? 'Instructor Led' : 'Self Paced'}
                    </span>
                  )}
                </div>
              </div>
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-all group-hover:bg-blue-50">
                <ChevronDown className={`w-5 h-5 text-blue-600 transition-transform duration-300 ${isModalOpen ? 'rotate-180' : ''}`} />
              </div>
            </button>
          </div>

          {/* Action Area */}
          <div className="max-w-md mx-auto">
            <button
              onClick={handleContinue}
              disabled={!selectedInstitution}
              className={`w-full py-5 px-8 rounded-2xl font-black text-lg tracking-wide shadow-xl transition-all duration-300 transform active:scale-[0.98] ${selectedInstitution
                ? 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white hover:opacity-90 shadow-blue-200 hover:-translate-y-1'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
                }`}
            >
              SECURE SIGN-IN
            </button>
            <div className="flex items-center justify-center gap-6 mt-8">
              <div className="flex items-center gap-1.5 text-slate-400">
                <div className="w-1.5 h-1.5 rounded-full bg-slate-300"></div>
                <span className="text-[11px] font-bold uppercase tracking-widest leading-none">Security Verified</span>
              </div>
              <div className="flex items-center gap-1.5 text-slate-400">
                <div className="w-1.5 h-1.5 rounded-full bg-slate-300"></div>
                <span className="text-[11px] font-bold uppercase tracking-widest leading-none">Cloud Sync Enabled</span>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Highlights Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 w-full max-w-4xl">
          {[
            { icon: BookOpen, label: 'Materials', color: 'text-blue-600' },
            { icon: Users, label: 'Expert Faculty', color: 'text-emerald-600' },
            { icon: Award, label: 'Accredited', color: 'text-amber-500' },
            { icon: Smartphone, label: 'Multi-App', color: 'text-indigo-600' }
          ].map((feature, i) => (
            <div key={i} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm transition-all hover:shadow-md hover:border-blue-100 flex flex-col items-center group">
              <feature.icon className={`w-6 h-6 ${feature.color} mb-3 transition-transform group-hover:scale-110`} />
              <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest leading-none">{feature.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Modern High-End Selection Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6 overflow-hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity duration-300 animate-in fade-in"
            onClick={() => setIsModalOpen(false)}
          ></div>

          {/* Modal Container */}
          <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-[2.5rem] shadow-[0_32px_64px_-12px_rgba(0,0,0,0.3)] relative z-10 flex flex-col overflow-hidden animate-in zoom-in-95 duration-300 border border-white/50">
            {/* Header */}
            <div className="p-8 border-b border-slate-50 relative shrink-0">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-2xl font-black text-slate-900 flex items-center gap-3">
                    Choose Institution
                    <span className="bg-blue-100 text-blue-700 text-[10px] font-black px-2 py-1 rounded-full uppercase tracking-tighter">
                      {institutions.length} AVAILABLE
                    </span>
                  </h3>
                  <p className="text-slate-400 font-bold text-sm mt-1">Select your learning partner from the list below</p>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="w-12 h-12 rounded-2xl bg-slate-50 hover:bg-slate-100 flex items-center justify-center text-slate-500 transition-colors shadow-inner"
                >
                  <ChevronDown className="w-6 h-6 rotate-180" />
                </button>
              </div>
            </div>

            {/* Content - Scrollable Grid */}
            <div className="flex-1 overflow-y-auto p-8 custom-scrollbar bg-slate-50/30">
              <div className="max-w-3xl mx-auto space-y-6">
                {institutions.map((institution) => {
                  const isFeatured = institution.name === 'Intuit Online Academy';
                  const isSelected = selectedInstitution?.id === institution.id;

                  return (
                    <button
                      key={institution.id}
                      onClick={() => handleInstitutionSelect(institution)}
                      className={`group relative w-full text-left p-8 rounded-[2rem] border-2 transition-all duration-500 ${isSelected
                        ? 'border-blue-600 bg-blue-50/50 shadow-2xl shadow-blue-100 ring-4 ring-blue-50'
                        : isFeatured
                          ? 'border-blue-200 bg-white hover:border-blue-400 hover:shadow-2xl hover:shadow-blue-100 transform hover:-translate-y-1'
                          : 'border-white bg-white hover:border-slate-200 hover:shadow-xl transform hover:-translate-y-1'
                        }`}
                    >
                      <div className="flex flex-col md:flex-row gap-8">
                        {/* Institution Logo / Icon */}
                        <div
                          className={`w-24 h-24 rounded-3xl flex items-center justify-center text-white text-4xl font-black shadow-2xl flex-shrink-0 transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 ${isFeatured ? 'ring-8 ring-blue-50' : ''
                            }`}
                          style={{ backgroundColor: institution.primaryColor || '#2563eb' }}
                        >
                          {institution.logo ? (
                            <img src={institution.logo} alt={institution.name} className="w-full h-full object-cover rounded-3xl" />
                          ) : (
                            institution.name.charAt(0)
                          )}
                        </div>

                        {/* Institution Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-3 mb-3">
                            <h4 className={`text-2xl font-black ${isSelected ? 'text-blue-900' : 'text-slate-900'}`}>
                              {institution.name}
                            </h4>
                            {isFeatured && (
                              <span className="bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[10px] font-black px-4 py-1.5 rounded-full shadow-lg border border-white/20 uppercase tracking-widest animate-pulse">
                                Premium Partner
                              </span>
                            )}
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                            <div className="flex items-center gap-2 text-slate-500 font-bold text-xs uppercase tracking-tight">
                              <span className={`px-2 py-0.5 rounded-md ${institution.learningMode === 'instructor_led' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                {institution.learningMode === 'instructor_led' ? 'Lecturer Led' : 'Self Paced Coursework'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-slate-400 font-bold text-xs uppercase tracking-tight">
                              <Smartphone className="w-4 h-4 text-blue-400" />
                              Multi-Device Sync Enabled
                            </div>
                          </div>

                          {/* Expanded Details */}
                          <div className="space-y-3 pt-4 border-t border-slate-100">
                            {institution.address && (
                              <div className="flex items-start gap-3">
                                <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                                  <Users className="w-3 h-3 text-slate-400" />
                                </div>
                                <span className="text-sm text-slate-500 leading-tight">{institution.address}</span>
                              </div>
                            )}
                            <div className="flex flex-wrap gap-x-6 gap-y-2">
                              {institution.website && (
                                <div className="flex items-center gap-2">
                                  <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                                    <BookOpen className="w-3 h-3 text-slate-400" />
                                  </div>
                                  <span className="text-sm text-blue-500 font-medium hover:underline">{institution.website.replace(/^https?:\/\//, '')}</span>
                                </div>
                              )}
                              {institution.phone && (
                                <div className="flex items-center gap-2">
                                  <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                                    <Smartphone className="w-3 h-3 text-slate-400" />
                                  </div>
                                  <span className="text-sm text-slate-500 font-medium">{institution.phone}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Selection Indicator */}
                        <div className="flex items-center justify-center shrink-0">
                          <div className={`w-12 h-12 rounded-2xl border-2 flex items-center justify-center transition-all duration-500 ${isSelected
                            ? 'bg-blue-600 border-blue-600 scale-110 shadow-2xl shadow-blue-300'
                            : 'border-slate-100 group-hover:border-blue-400 group-hover:bg-blue-50'
                            }`}>
                            {isSelected && <Check className="w-6 h-6 text-white" />}
                          </div>
                        </div>
                      </div>

                      {/* Featured Border Effect */}
                      {isFeatured && (
                        <div className="absolute inset-0 rounded-[2rem] border-2 border-blue-400/20 pointer-events-none group-hover:border-blue-400/40 transition-colors"></div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstitutionSelection;
